'use client';

import { FormatIcu } from '@tolgee/format-icu';
import { TolgeeProvider } from '@tolgee/react';
import { Tolgee, DevTools } from '@tolgee/web';
import type { TolgeeStaticData } from '@tolgee/web';

import { notifyLanguageChange } from '@/hooks/useUserLanguage';

import deTranslations from '../../public/i18n/de.json';
import enTranslations from '../../public/i18n/en.json';

interface TranslationData {
  [key: string]: string | TranslationData;
}

function loadStaticTranslations(): TolgeeStaticData {
  return {
    en: enTranslations as TranslationData,
    de: deTranslations as TranslationData,
  };
}

// Always use 'en' as the initial language for both server and client
// This ensures hydration matches, then client can change language after hydration
const initialLanguage = 'en';

// Create Tolgee instance with DevTools controlled by NEXT_PUBLIC_TOLGEE_DEV_TOOLS
const tolgeeBuilder = Tolgee();

// Only include DevTools in development mode when explicitly enabled and
// a real Tolgee key is present. Placeholder keys can trigger in-context
// wrappers that cause hydration mismatches and unstable cursor behavior.
const tolgeeApiKey = process.env.NEXT_PUBLIC_TOLGEE_API_KEY;
const hasUsableTolgeeApiKey =
  typeof tolgeeApiKey === 'string' &&
  tolgeeApiKey.trim().length > 0 &&
  tolgeeApiKey !== 'dummy' &&
  tolgeeApiKey !== 'your_tolgee_api_key';
const isDevToolsEnabled =
  process.env.NODE_ENV === 'development' &&
  process.env.NEXT_PUBLIC_TOLGEE_DEV_TOOLS === 'true' &&
  hasUsableTolgeeApiKey;

if (isDevToolsEnabled) {
  tolgeeBuilder.use(DevTools());
}

// FormatIcu supports ICU message syntax (select/plural) as well as simple
// {param} interpolation. FormatSimple cannot parse ICU and throws FormatError
// at runtime on messages like "{count, plural, one {…} other {…}}".
const tolgee = tolgeeBuilder.use(FormatIcu()).init({
  language: initialLanguage, // Always start with English for consistent hydration
  apiUrl: process.env.NEXT_PUBLIC_TOLGEE_API_URL,
  apiKey: process.env.NEXT_PUBLIC_TOLGEE_API_KEY,
  fallbackLanguage: 'en',
  staticData: loadStaticTranslations(),
});

// After initialization, we can set up client-side language detection
if (typeof window !== 'undefined') {
  // This runs only on the client after hydration
  setTimeout(() => {
    const storedLang = localStorage.getItem('user_language');
    if (storedLang && (storedLang === 'en' || storedLang === 'de')) {
      void tolgee.changeLanguage(storedLang);
    } else {
      // If no stored language, try to use browser language
      const browserLang = navigator.language.split('-')[0];
      const detectedLang = browserLang === 'de' ? 'de' : 'en';
      if (detectedLang !== initialLanguage) {
        void tolgee.changeLanguage(detectedLang);
        localStorage.setItem('user_language', detectedLang);
        notifyLanguageChange();
      }
    }
  }, 0);
}

export { tolgee, TolgeeProvider };
